// What a session or a proposal waits on (Poulpe-367): its runner, while that
// runner is offline. The runners come live from the feed, so the line goes
// as soon as the runner says hello again.
import type { Landing, Proposal, Runner, Session, Waiting } from '../api/types'
import { clockTime, exactTime } from '../components/Time'
import { useBoardState } from '../store/context'

/** The steps at which a proposal's own runner works on it: it pushes the head, checks it, and builds and checks its candidate. */
const RUNNER_STEPS: ReadonlySet<Proposal['step']> = new Set<Proposal['step']>(['head', 'check', 'landing'])

/** The runner `id` names, while it is offline; null when it is online or unknown. */
export function offlineRunner(runners: ReadonlyMap<string, Runner>, id: string | null | undefined): Runner | null {
  const runner = id ? runners.get(id) : undefined
  return runner && !runner.connected ? runner : null
}

/** The runner a session waits on: its own, offline, while it has not ended. */
export function sessionWaitsOn(runners: ReadonlyMap<string, Runner>, session: Session): Runner | null {
  return session.state === 'ended' ? null : offlineRunner(runners, session.runner_id)
}

/** What a running landing waits for from a runner: its candidate, and the full check of it. */
const RUNNER_WAITS: ReadonlySet<Waiting['for']> = new Set<Waiting['for']>(['candidate', 'full_check'])

/**
 * The runner an open proposal waits on, offline, at a step that runner works: its worktree session's. In a landing, the
 * landing's first proposal's runner builds and checks the candidate for every member (queue/mod.rs there), so that one,
 * and only while the landing runs and waits for its candidate or its full check: an approval, GitHub, or another
 * landing needs no runner.
 */
export function proposalWaitsOn(
  runners: ReadonlyMap<string, Runner>,
  sessions: ReadonlyMap<string, Session>,
  proposals: ReadonlyMap<string, Proposal>,
  proposal: Proposal,
  landing: Landing | null,
): Runner | null {
  if (proposal.state !== 'open' || !RUNNER_STEPS.has(proposal.step)) return null
  let worker: Proposal | undefined = proposal
  if (proposal.step === 'landing') {
    const waiting = landing?.state === 'running' ? landing.waiting_for : null
    if (!landing || !waiting || !RUNNER_WAITS.has(waiting.for)) return null
    worker = proposals.get(landing.proposal_id)
  }
  return worker ? offlineRunner(runners, sessions.get(worker.worktree_session_id ?? worker.session_id)?.runner_id) : null
}

/** One line: the runner waited on, offline since when. */
export function WaitsOn({ runner }: { runner: Runner | null }) {
  if (!runner) return null
  return (
    <p className="waits-on" role="status">
      Waits on runner {runner.name}, offline
      {runner.since && (
        <>
          {' '}since{' '}
          <time dateTime={runner.since} title={exactTime(runner.since)}>
            {clockTime(runner.since, Date.now())}
          </time>
        </>
      )}
      .
    </p>
  )
}

/** The line for `session`, from the live runners. */
export function SessionWaitsOn({ session }: { session: Session }) {
  const { runners } = useBoardState()
  return <WaitsOn runner={sessionWaitsOn(runners, session)} />
}

/** The line for `proposal`, in `landing` if it lands, from the live runners, sessions and proposals. */
export function ProposalWaitsOn({ proposal, landing }: { proposal: Proposal; landing: Landing | null }) {
  const { runners, sessions, proposals } = useBoardState()
  return <WaitsOn runner={proposalWaitsOn(runners, sessions, proposals, proposal, landing)} />
}
